import { Alert, Table, TableProps } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import { Breakpoint } from 'antd/lib/_util/responsiveObserver'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import { PropertyLabel } from '../../../common/propertyDetailTypes'
import {
  LabelConnectionFragmentFragment,
  usePropertyLabelTableQuery,
} from '../../../graphql/generated'
import {
  labelSorter,
  stringSorter,
  utcDateSorter,
} from '../../utils/generalTableHelpers'
import { tablePagination } from '../../utils/layoutConst'
import { useColumnFilters } from '../../utils/useColumnFilters'
import { usePropertyTableNoteCheck } from '../../utils/usePropertyTableNoteCheck'
import { useTableRowSelection } from '../../utils/useTableRowSelection'
import { Min } from '../DateFormat/DateFormat'
import { LvDetailDrawerLink } from '../LvDetailDrawerLink/LvDetailDrawerLink'
import { PropertyDetailLink } from '../PropertyDetailLink/PropertyDetailLink'
import { PropertyLabelTableBulkEdit } from '../PropertyLabelTableBulkEdit/PropertyLabelTableBulkEdit'
import { PropertyNoteModalButton } from '../PropertyNoteModalButton/PropertyNoteModalButton'
import { TableCellLabels } from '../TableCellLabels/TableCellLabels'
import { TableSummaryExport } from '../TableSummaryExport/TableSummaryExport'

export interface PropertyLabelTableProps {
  labelId: string
  onActionRefetch?: () => void
  tableProps?: TableProps<TableRowType>
}

export type TableRowType = PropertyLabel & {
  labels?: LabelConnectionFragmentFragment | null
}

const tableId = 'propertyLabel-table'
const scrollX = { x: 1000 }

export const PropertyLabelTable: FunctionComponent<PropertyLabelTableProps> =
  props => {
    const { searchFilter, dateFilter, propertyTypeFilter, labelFilter } =
      useColumnFilters<TableRowType>()

    const { selectedRows, rowSelection, resetSelection } =
      useTableRowSelection<TableRowType>({
        limit: 100,
        preserveSelectedRowKeys: true,
        getRowKey: record => record.id,
      })

    const [queryResult, refetch] = usePropertyLabelTableQuery({
      variables: { labelId: props.labelId },
    })

    const dataSource = useMemo(
      () =>
        queryResult.data?.allLabelConnections?.nodes
          .map(
            item =>
              item?.propertyLabelByPropertyLabelId as unknown as TableRowType
          )
          .filter(Boolean),
      [queryResult.data?.allLabelConnections?.nodes]
    ) as TableRowType[]

    const propertyIds = useMemo(
      () => dataSource?.map(item => item.propertyId) || [],
      [dataSource]
    )
    const propertyNotes = usePropertyTableNoteCheck(propertyIds)

    const exportData = useMemo(
      () =>
        dataSource?.map(item => {
          const propertyNote = propertyNotes?.[item.propertyId]
          return {
            ...item,
            propertyNote: propertyNote?.note,
            labels: undefined,
          }
        }) || [],
      [dataSource, propertyNotes]
    )

    const columns = useMemo<ColumnsType<TableRowType>>(
      () => [
        {
          title: 'ID',
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Přidáno',
          dataIndex: 'createdAt',
          defaultSortOrder: 'descend',
          className: 'w-32',
          sorter: utcDateSorter('createdAt'),
          ...dateFilter('createdAt'),
          render: createdAt => <Min>{createdAt}</Min>,
        },
        {
          title: 'Štítek',
          dataIndex: 'labels',
          sorter: labelSorter('labels'),
          ...labelFilter('labels'),
          render: (labels, record) => (
            <TableCellLabels
              labels={labels}
              labelEntityType="propertyLabelId"
              recordId={record.id}
              refetch={refetch}
              modalTitle={record.propertyName}
            />
          ),
        },
        {
          title: 'Typ',
          dataIndex: 'propertyType',
          className: 'w-12',
          sorter: stringSorter('propertyType'),
          ...propertyTypeFilter('propertyType'),
        },
        {
          title: 'Název nemovitosti',
          dataIndex: 'propertyName',
          sorter: stringSorter('propertyName'),
          ...searchFilter('propertyName'),
          render: (name, record) => (
            <PropertyDetailLink
              propertyId={record.propertyId}
              propertyType={record.propertyType}
              drawerTarget
              text={name}
            />
          ),
        },
        {
          title: 'Poznámka',
          dataIndex: 'propertyId',
          key: 'note',
          className: 'w-64',
          render: id => (
            <PropertyNoteModalButton
              className="truncate inline-block w-64"
              note={propertyNotes?.[id]}
            />
          ),
        },
        {
          title: 'LV',
          dataIndex: 'propertyLv',
          className: 'w-16 !text-right',
          sorter: stringSorter('propertyLv'),
          ...searchFilter('propertyLv'),
          render: (propertyLv, record) => (
            <LvDetailDrawerLink
              lvCode={record.propertyCadastralCode}
              lvNumber={propertyLv}
            />
          ),
        },
        {
          title: 'Katastrální území',
          dataIndex: 'propertyCadastralName',
          sorter: stringSorter('propertyCadastralName'),
          ...searchFilter('propertyCadastralName'),
        },
      ],
      [
        dateFilter,
        labelFilter,
        propertyNotes,
        propertyTypeFilter,
        refetch,
        searchFilter,
      ]
    )

    const summary = useCallback(
      () => (
        <TableSummaryExport
          total={dataSource?.length || 0}
          colSpan={7}
          url="propertyLabel/propertyXlsx"
          fileName="Nemovitosti"
          dataSource={exportData}
        />
      ),
      [dataSource?.length, exportData]
    )

    if (queryResult.error)
      return (
        <Alert
          message="Chyba při načítání nemovitostí"
          description="Je nám líto, ale nepodařilo se najít detaily nemovitostí s přiřazeným štítkem."
          type="error"
          showIcon
        />
      )

    return (
      <>
        <PropertyLabelTableBulkEdit
          selectedRows={selectedRows as TableRowType[]}
          refetch={() => {
            resetSelection()
            refetch()
          }}
        />
        <Table<TableRowType>
          rowKey="id"
          size="small"
          id={tableId}
          dataSource={dataSource}
          columns={columns}
          loading={queryResult.fetching}
          pagination={tablePagination}
          scroll={scrollX}
          summary={summary}
          rowSelection={rowSelection}
          {...props.tableProps}
        />
      </>
    )
  }
